import type { ReactNode } from 'react'

import { ChromeToolbarButton, ChromeToolbarDangerButton } from './ChromeActions'
import { chromeFieldBaseClass } from './ChromeSurface'
import { IconLibraryAllFolders } from './nav-icons'

function slotRound(angular: boolean): string {
  return angular ? 'rounded-none' : 'rounded-[var(--radius-md)]'
}

export type ChromeMediaUploadSlotProps = {
  angular: boolean
  hasMedia: boolean
  preview?: ReactNode
  uploading?: boolean
  disabled?: boolean
  /** 空态提示 */
  emptyLabel?: string
  /** 有图时主操作文案 */
  selectLabel?: string
  deleteLabel?: string
  /** 打开图库选择器 */
  onSelect: () => void
  onDelete?: () => void
  clickEmptyToSelect?: boolean
  emptyIcon?: ReactNode
  className?: string
  density?: 'default' | 'compact'
}

/**
 * 媒体槽：宽屏横向预览 + 底部「从图库选择 / 删除」；空态点击进入图库选择器。
 */
export function ChromeMediaUploadSlot({
  angular,
  hasMedia,
  preview,
  uploading = false,
  disabled = false,
  emptyLabel = '点击从图库选择',
  selectLabel = '从图库选择',
  deleteLabel = '删除',
  onSelect,
  onDelete,
  clickEmptyToSelect = true,
  emptyIcon,
  className = '',
  density = 'default',
}: ChromeMediaUploadSlotProps) {
  const aspect = density === 'compact' ? 'aspect-[5/4] max-h-40' : 'aspect-[16/10] max-h-52 sm:max-h-56'

  const handleEmptyClick = () => {
    if (disabled || uploading || !clickEmptyToSelect) return
    onSelect()
  }

  return (
    <div
      className={[
        chromeFieldBaseClass(angular),
        'overflow-hidden p-0',
        disabled ? 'pointer-events-none opacity-55' : '',
        className,
      ].join(' ')}
    >
      <div
        className={[
          'relative w-full overflow-hidden bg-surface-muted/35',
          aspect,
          slotRound(angular),
        ].join(' ')}
      >
        {hasMedia && preview ? (
          <button
            type="button"
            className="absolute inset-0 z-0 cursor-pointer border-0 bg-transparent p-0"
            onClick={onSelect}
            disabled={disabled || uploading}
            aria-label={selectLabel}
          >
            <span className="pointer-events-none block h-full w-full">{preview}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={handleEmptyClick}
            disabled={disabled || uploading}
            className={[
              'absolute inset-0 flex w-full flex-col items-center justify-center gap-2 border-0 bg-transparent px-4 text-center',
              'border border-dashed border-line bg-surface-muted/25 transition',
              'hover:bg-surface-muted/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
              slotRound(angular),
            ].join(' ')}
          >
            <span
              className={[
                'flex size-11 items-center justify-center border border-line bg-[var(--menu-solid)] text-ink-soft',
                angular ? 'rounded-none' : 'rounded-full',
              ].join(' ')}
            >
              {emptyIcon ?? <IconLibraryAllFolders className="h-5 w-5" aria-hidden />}
            </span>
            <span className="text-xs font-medium text-ink-soft">{emptyLabel}</span>
          </button>
        )}

        {hasMedia ? (
          <div
            className={[
              'absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center justify-end gap-2',
              'bg-gradient-to-t from-[color-mix(in_srgb,var(--panel)_92%,transparent)] to-transparent px-3 pb-3 pt-10',
            ].join(' ')}
          >
            <div className="flex flex-wrap items-center justify-end gap-2">
              <ChromeToolbarButton
                type="button"
                className="h-8 px-3 text-xs shadow-sm"
                disabled={uploading}
                onClick={onSelect}
              >
                {uploading ? '处理中…' : selectLabel}
              </ChromeToolbarButton>
              {onDelete ? (
                <ChromeToolbarDangerButton
                  type="button"
                  className="h-8 px-3 text-xs shadow-sm"
                  onClick={onDelete}
                >
                  {deleteLabel}
                </ChromeToolbarDangerButton>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
